import { Fragment, useState } from 'react';
import { orderBy } from 'lodash-es';
import {
  Feeds2,
  Feeds4New,
  SupplementsContainer,
  TableSetting,
} from '@hungryroot/icons';
import {
  useMealsSection,
  usePlanNameHeaderIcon,
  DINNER_STABLE_NAME_ID,
  LUNCH_STABLE_NAME_ID,
  PlanSection,
  findContainer,
  findPlanComponent,
  findPlanComponentByContainerId,
  OccasionContainer,
  DINNER_CONTAINER_ID,
  SNACKS_CONTAINER_ID,
  SWEETS_CONTAINER_ID,
  OtherGroceries,
  getEditContainerQuantityBlockContent,
  getOccasionContainerSubtitle,
  getContainerIdToPlanComponent,
} from '@hungryroot/plan';

import { Paragraph } from '@hungryroot/ramen';
import {
  type PlanServiceNestedUniverse,
  type PlanServicePlanComponents,
  type PricingServicePricedContainer,
  type ServingSize,
  type Occasion,
  type PricingServiceProductSubscriptionContainer,
} from '@hungryroot/rest';
import {
  SubscriptionContainerBlock,
  getCustomerProductSubscriptionCopy,
} from '@hungryroot/supplements';
import { EditContainerQuantityBlock } from '../EditContainerQuantityBlock/EditContainerQuantityBlock';
import { EditRecipeNumberBlockContainer } from '../EditRecipeNumberBlockContainer/EditRecipeNumberBlockContainer';
import { EditRecipeSizeBlock } from '../EditRecipeSizeBlock/EditRecipeSizeBlock';
import styles from './PlanContent.module.css';

const BOX_BUILDER_PRICING_VERSION = 18;

export type MealContainerSubmitProps = {
  nextNumLunch: number;
  nextNumDinner: number;
  numLunch: number;
  numDinner: number;
  occasionId: number;
  containerId: number;
  dinnerContainerId: number;
  servingSize: ServingSize;
};

type onRecipeSizeContainerSubmitProps = {
  value: ServingSize;
  numLunch: number;
  numDinner: number;
};

type onNonMealContainerSubmitProps = {
  value: string;
  containerId: number;
  occasionId: number;
  occasionGroupId: number;
};

type onContainerQuantitySubmitProps = {
  quantity: number;
  servingSize: ServingSize;
  dinnerServingSize: ServingSize;
  containerId: number;
  occasionId: number;
  occasionGroupId: number;
};

type Props = {
  id: number;
  name: string;
  editDisabled?: boolean;
  occasions: Occasion[];
  subHeader: string | null;
  tooltip: string | null;
  index: number;
  customerId: number;
  onMealContainerSubmit: (props: MealContainerSubmitProps) => void;
  onRecipeSizeContainerSubmit: (
    props: onRecipeSizeContainerSubmitProps
  ) => void;
  onNonMealContainerSubmit: (props: onNonMealContainerSubmitProps) => void;
  onContainerQuantitySubmit: (props: onContainerQuantitySubmitProps) => void;
  planComponents: PlanServicePlanComponents;
  containerIdToContainer: Record<number, PricingServicePricedContainer>;
  customerSubscriptionContainers?: Record<
    number,
    PricingServiceProductSubscriptionContainer
  >;
  planServiceNestedUniverseData: PlanServiceNestedUniverse;
  confirmButtonText: string;
  showSubsections?: boolean;
};

export const PlanContent = ({
  id,
  name,
  editDisabled,
  occasions,
  subHeader,
  tooltip,
  index,
  customerId,
  onMealContainerSubmit,
  onRecipeSizeContainerSubmit,
  onNonMealContainerSubmit,
  onContainerQuantitySubmit,
  planComponents,
  containerIdToContainer,
  customerSubscriptionContainers,
  planServiceNestedUniverseData,
  confirmButtonText,
  showSubsections = true,
}: Props) => {
  const lunchComponent = findPlanComponent(
    planComponents.plan_components,
    LUNCH_STABLE_NAME_ID
  );

  const dinnerComponent = findPlanComponent(
    planComponents.plan_components,
    DINNER_STABLE_NAME_ID
  );

  const dinnerContainer = findContainer(
    DINNER_STABLE_NAME_ID,
    planServiceNestedUniverseData
  );

  const lunchContainer = findContainer(
    LUNCH_STABLE_NAME_ID,
    planServiceNestedUniverseData
  );

  const containerIdToPlanComponent =
    getContainerIdToPlanComponent(planComponents);

  const lunchDinnerPricingVersion =
    dinnerComponent?.pricing_configurations.RECIPE_QUANTITY?.version ||
    lunchComponent?.pricing_configurations.RECIPE_QUANTITY?.version;

  const mealsSection = useMealsSection(lunchDinnerPricingVersion || 0);

  const lunchRefreshUi = usePlanNameHeaderIcon(planComponents);

  // pricing strategy and serving size is always the same for lunch and dinner
  // use this when adapting for different pricing strategies
  // const pricingStrategy = dinnerContainer?.pricing_strategy as PricingConfigurationKeys;
  const servingSize = (dinnerComponent || lunchComponent)
    ?.pricing_configurations.RECIPE_QUANTITY?.serving_size as ServingSize;

  const dinnerServingSize = dinnerComponent?.pricing_configurations
    .RECIPE_QUANTITY?.serving_size as ServingSize;

  const [nextServingSize, setNextServingSize] =
    useState<ServingSize>(servingSize);

  const numLunch = lunchComponent?.container_quantity || 0;
  const numDinner = dinnerComponent?.container_quantity || 0;

  return (
    <PlanSection
      containerClassName={showSubsections ? styles.section : ''}
      titleClassName={styles['section-title']}
      title={name}
      description={subHeader}
      tooltip={tooltip && <Paragraph>{tooltip}</Paragraph>}
      showSubsections={showSubsections}
    >
      {occasions.map(
        ({ containers, display_name, name, id: occasionId, image_url }) => {
          // the above also returns the image url and display_name, but we're
          // ignoring it for now because the recipe section is an exception to the rule

          // only the first container matters for now until we scale the UI to handle
          // multiple containers
          const containerId = containers[0].id;
          const containerPricingStrategy = containers[0].pricing_strategy;

          // this assumes the first section is always "Meals"  which is used to
          // configure "Recipes per week" and "Recipe size"
          if (index === 0) {
            const dinnerContainerId = dinnerContainer?.id || 0;

            const commonProps = {
              className: styles.block,
              containerId: containerId,
              customerId: customerId,
              // missing recipe price
              servingSize,
              confirmButtonText,
              editDisabled,
            };

            return (
              mealsSection &&
              mealsSection({
                defaultComponents: (
                  <Fragment key="first-block">
                    <EditRecipeNumberBlockContainer
                      {...commonProps}
                      id="recipes-per-week"
                      heading="Recipes per week"
                      initialValues={{
                        lunchRecipes: numLunch,
                        dinnerRecipes: numDinner,
                      }}
                      icon={<TableSetting />}
                      description={`${
                        numLunch + numDinner
                      } recipes (${numLunch} lunch, ${numDinner} dinner)`}
                      onSubmit={({
                        numLunch: nextNumLunch,
                        numDinner: nextNumDinner,
                      }: {
                        numLunch: number;
                        numDinner: number;
                      }) => {
                        onMealContainerSubmit({
                          nextNumLunch,
                          nextNumDinner,
                          numLunch,
                          numDinner,
                          occasionId,
                          containerId,
                          dinnerContainerId,
                          servingSize,
                        });
                      }}
                    ></EditRecipeNumberBlockContainer>

                    <EditRecipeSizeBlock
                      {...commonProps}
                      id="recipes-size"
                      heading="Recipe size"
                      description={`${servingSize} servings / recipe`}
                      icon={servingSize === 2 ? <Feeds2 /> : <Feeds4New />}
                      onSubmit={(value: ServingSize) => {
                        onRecipeSizeContainerSubmit({
                          value,
                          numLunch,
                          numDinner,
                        });
                      }}
                    />
                  </Fragment>
                ),
                pricingVersion15andAboveComponents: lunchContainer?.id && (
                  <Fragment key="first-block">
                    {orderBy(containers, 'sort_order').map((container) => {
                      const containerPricingStrategy =
                        containerIdToContainer[container.id]?.RECIPE_QUANTITY;
                      const component = findPlanComponentByContainerId(
                        planComponents.plan_components,
                        container.id
                      );

                      const blockContent = getEditContainerQuantityBlockContent(
                        container.id,
                        nextServingSize,
                        lunchDinnerPricingVersion
                      );

                      if (blockContent === null) {
                        return null;
                      }

                      const { min, inputLabel, modalBody, icon } = blockContent;

                      return (
                        <EditContainerQuantityBlock
                          key={container.id}
                          pricingVersion={lunchDinnerPricingVersion}
                          containerName={container.name}
                          editDisabled={editDisabled}
                          className={styles.block}
                          customerId={customerId}
                          containerId={container.id}
                          initialValue={component?.container_quantity || 0}
                          servingSize={(nextServingSize as ServingSize) || 0}
                          minQuantity={min}
                          icon={icon}
                          heading={container.display_name || container.name}
                          description={`${
                            containerPricingStrategy?.servings || 0
                          } servings`}
                          inputLabel={inputLabel}
                          modalHeading={`Edit ${
                            container.display_name || container.name
                          } plan`}
                          onCancel={() => {
                            // revert the set serving size if closed
                            container.id === DINNER_CONTAINER_ID
                              ? setNextServingSize(servingSize)
                              : undefined;
                          }}
                          modalBody={modalBody}
                          onServingSizeChange={
                            // Serving size toggle rendered in the dinner editing block only
                            container.id === DINNER_CONTAINER_ID
                              ? (value: ServingSize) =>
                                  setNextServingSize(value)
                              : undefined
                          }
                          onSubmit={(
                            nextQuantity: number,
                            nextServingSizeValue?: ServingSize
                          ) => {
                            // Serving size can only be changed in the dinner block. If it has been changed, use the new value for servingSize and dinnerServingSize, otherwise use dinnerServingSize to keep consistent across all "Meal" containers.

                            onContainerQuantitySubmit({
                              quantity: nextQuantity,
                              servingSize:
                                nextServingSizeValue || dinnerServingSize,
                              dinnerServingSize:
                                nextServingSizeValue || dinnerServingSize,
                              containerId: container.id,
                              occasionId,
                              occasionGroupId: id,
                            });
                          }}
                          confirmButtonText={confirmButtonText}
                        />
                      );
                    })}
                  </Fragment>
                ),
              })
            );
          }

          const planComponent = containerIdToPlanComponent?.[containerId];

          const container = containerIdToContainer?.[containerId];

          if (
            // these modals only apply for these containers
            (containerId === SNACKS_CONTAINER_ID ||
              containerId === SWEETS_CONTAINER_ID) &&
            // pricing version specific to box builder experience
            // we can use the lunch and dinner container pricing version as they should be consistent with the other containers
            // if a non-meal container is not selected, it won't be returned by the API
            // thus we can't use that as the source of truth for the pricing version
            lunchDinnerPricingVersion === BOX_BUILDER_PRICING_VERSION
          ) {
            return (
              <OtherGroceries
                customerId={customerId}
                containerId={containerId}
                editDisabled={editDisabled}
                className={styles.block}
                key={`${id}-${occasionId}`}
                heading={display_name || name}
                modalHeading={`Edit ${display_name || name} plan`}
                onActionClick={(value) => {
                  onNonMealContainerSubmit({
                    value,
                    containerId,
                    occasionId,
                    occasionGroupId: id,
                  });
                }}
                description={
                  // this won't exist if it's not part of a customer's plan
                  container?.name || ''
                }
                icon={lunchRefreshUi?.getIcon({
                  name,
                  imageUrl: image_url,
                  className: styles.icon,
                })}
                initialValue={planComponent?.container_quantity}
              ></OtherGroceries>
            );
          }
          if (containerPricingStrategy === 'PRODUCT_SUBSCRIPTION') {
            const containerDisplayName =
              containers[0].display_name || containers[0].name;
            return (
              <SubscriptionContainerBlock
                key={`${id}-${occasionId}`}
                editDisabled={editDisabled}
                className={styles.block}
                customerId={customerId}
                occasionGroupId={id}
                occasionId={occasionId}
                containerId={containerId}
                containerName={containerDisplayName}
                heading={display_name || name}
                description={getCustomerProductSubscriptionCopy(
                  containerId,
                  customerSubscriptionContainers
                )}
                icon={<SupplementsContainer />}
                isNew
              />
            );
          }

          return (
            <OccasionContainer
              editDisabled={editDisabled}
              customerId={customerId}
              title={`Edit ${display_name || name} plan`}
              subtitle={getOccasionContainerSubtitle(containerId)}
              containerId={containerId}
              containerName={name}
              className={styles.block}
              key={`${id}-${occasionId}`}
              heading={display_name || name}
              onSubmit={(value) => {
                onNonMealContainerSubmit({
                  value,
                  containerId,
                  occasionId,
                  occasionGroupId: id,
                });
              }}
              icon={lunchRefreshUi?.getIcon({
                name,
                imageUrl: image_url,
                className: styles.icon,
              })}
              initialValue={planComponent?.container_quantity}
              confirmButtonText={confirmButtonText}
            ></OccasionContainer>
          );
        }
      )}
    </PlanSection>
  );
};
